import { useEffect } from 'react';

import { Platform } from 'react-native';

import { RunningExperiments, useExperiments } from 'state/experiments';

const CONTROL_VARIANT = 'control';
const TREATMENT_VARIANT = 'treatment';

// Liquid Glass (and the SwiftUI NativeTabs it requires) ships starting iOS 26.
const MIN_TREATMENT_IOS_VERSION = 26;

export type NativeTabBarExperimentGate = {
  isReady: boolean;
  isTreatment: boolean;
  hasResolvedVariant: boolean;
  isPlatformEligible: boolean;
  variantValue?: string;
};

type ResolveNativeTabBarExperimentGateArgs = {
  isExperimentLoading: boolean;
  variantValue?: string;
  platformOS: typeof Platform.OS;
  platformVersion: typeof Platform.Version;
};

/**
 * Pure gate used by the hook and unit tests.
 * NativeTabs (Liquid Glass) requires iOS {@link MIN_TREATMENT_IOS_VERSION}+ — Android and
 * older iOS always stay on the classic custom tab bar regardless of the assigned variant.
 * An ineligible platform can never render treatment, so there is nothing worth waiting on
 * Amplitude for: it's reported ready (and not treatment) immediately, even while the
 * experiment is still loading. Eligible platforms are ready once a control/treatment variant
 * exists, or once Amplitude finishes loading; the safe default while unresolved is not
 * treatment (current custom tab bar).
 */
export const resolveNativeTabBarExperimentGate = ({
  isExperimentLoading,
  variantValue,
  platformOS,
  platformVersion,
}: ResolveNativeTabBarExperimentGateArgs): NativeTabBarExperimentGate => {
  // Platform.Version can include a patch component (e.g. '26.0.1'), for which
  // Number(...) is NaN - parseInt to read just the major version, same as
  // src/utils/image/index.ts does for its own iOS version checks.
  const isPlatformEligible =
    platformOS === 'ios' && parseInt(String(platformVersion), 10) >= MIN_TREATMENT_IOS_VERSION;
  const hasResolvedVariant = variantValue === CONTROL_VARIANT || variantValue === TREATMENT_VARIANT;
  const isReady = !isPlatformEligible || hasResolvedVariant || !isExperimentLoading;
  const isTreatment = isPlatformEligible && variantValue === TREATMENT_VARIANT;

  return {
    isReady,
    isTreatment,
    hasResolvedVariant,
    isPlatformEligible,
    variantValue,
  };
};

// Once the gate first resolves, the native-vs-classic decision (and everything that must stay
// consistent with whichever navigator is actually mounted — headers, content-inset clearance,
// exposure logging) locks for the rest of the app session. A later login/logout can resolve to
// a different variant, but the mounted navigator never swaps mid-session (that would reset the
// selected tab and any nested navigation state), so every consumer of this hook must see the
// SAME locked snapshot rather than each independently deciding when to lock. A per-component
// lock wouldn't work here: a lazily-mounted screen (e.g. my-code, only mounted when that tab is
// first visited) could otherwise lock onto a later identity's assignment than what the tab
// layout itself locked at app start.
let lockedGate: NativeTabBarExperimentGate | null = null;
let hasExposedLockedGate = false;

/** Test-only escape hatch: clears the module-level lock so each test starts from a clean gate. */
export const unstable_resetNativeTabBarExperimentLockForTests = () => {
  lockedGate = null;
  hasExposedLockedGate = false;
};

/**
 * Reads the native tab bar experiment WITHOUT logging exposure.
 * Exposure is logged separately via {@link useExposeNativeTabBarExperiment}
 * when the tab layout actually mounts.
 */
export const useEnableNativeTabBarExperiment = (): NativeTabBarExperimentGate => {
  const { getVariantValue, isLoading } = useExperiments();
  const variantValue = getVariantValue(RunningExperiments.EnableNativeTabBar)?.value;

  const liveGate = resolveNativeTabBarExperimentGate({
    isExperimentLoading: isLoading,
    variantValue,
    platformOS: Platform.OS,
    platformVersion: Platform.Version,
  });

  if (lockedGate === null && liveGate.isReady) {
    lockedGate = liveGate;
  }

  return lockedGate ?? liveGate;
};

/**
 * Logs Amplitude exposure for control and treatment once the tab layout mounts. Fires at most
 * once per app session (guarded at module scope, independent of any later remount), in lockstep with
 * the gate's own lock above — once the navigator choice is locked, a later identity resolving
 * to a different variant must not log an exposure that contradicts what's actually rendered.
 */
export const useExposeNativeTabBarExperiment = (shouldExpose: boolean) => {
  const { logExposure } = useExperiments();

  useEffect(() => {
    if (!shouldExpose || hasExposedLockedGate) {
      return;
    }

    hasExposedLockedGate = true;
    logExposure(RunningExperiments.EnableNativeTabBar);
  }, [shouldExpose, logExposure]);
};
